import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DigitalLibraryPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    subject: '',
    level: ''
  });
  
  // Form state for new resource
  const [newResource, setNewResource] = useState({
    title: '',
    description: '',
    type: 'book',
    category: 'textbook',
    subject: 'Physics',
    topics: '',
    level: 'intermediate',
    language: 'TR',
    publisher: '',
    author: '',
    url: '',
    isbn: '',
    duration_minutes: '',
    page_count: ''
  });

  useEffect(() => {
    fetchResources();
  }, [filters]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/library/resources`, {
        params: {
          type: filters.type || undefined,
          subject: filters.subject || undefined,
          level: filters.level || undefined
        },
        withCredentials: true
      });
      
      setResources(response.data.resources || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateResource = async (e) => {
    e.preventDefault();
    
    try {
      // Convert topics string to array
      const resourceData = {
        ...newResource,
        topics: newResource.topics.split(',').map(t => t.trim()).filter(t => t),
        duration_minutes: newResource.duration_minutes ? parseInt(newResource.duration_minutes) : null,
        page_count: newResource.page_count ? parseInt(newResource.page_count) : null
      };
      
      await axios.post(
        `${API}/library/resources`,
        resourceData,
        { withCredentials: true }
      );
      
      alert(language === 'tr' ? '✅ Kaynak başarıyla eklendi!' : '✅ Resource added successfully!');
      setShowAddModal(false);
      fetchResources();
      
      // Reset form
      setNewResource({
        title: '',
        description: '',
        type: 'book',
        category: 'textbook',
        subject: 'Physics',
        topics: '',
        level: 'intermediate',
        language: 'TR',
        publisher: '',
        author: '',
        url: '',
        isbn: '',
        duration_minutes: '',
        page_count: ''
      });
    } catch (error) {
      console.error('Error creating resource:', error);
      alert(language === 'tr' 
        ? `❌ Hata: ${error.response?.data?.detail || error.message}` 
        : `❌ Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm(language === 'tr' ? 'Bu kaynağı silmek istediğinizden emin misiniz?' : 'Are you sure you want to delete this resource?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/library/resources/${resourceId}`, {
        withCredentials: true
      });
      
      alert(language === 'tr' ? '✅ Kaynak silindi!' : '✅ Resource deleted!');
      fetchResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert(language === 'tr' ? '❌ Silme hatası!' : '❌ Delete failed!');
    }
  };

  const getResourceIcon = (type) => {
    const icons = {
      book: '📚',
      pdf: '📄',
      video: '🎥',
      interactive: '🎮'
    };
    return icons[type] || '📦';
  };

  const getResourceTypeName = (type) => {
    const names = {
      book: language === 'tr' ? 'Kitap' : 'Book',
      pdf: 'PDF',
      video: language === 'tr' ? 'Video' : 'Video',
      interactive: language === 'tr' ? 'İnteraktif' : 'Interactive'
    };
    return names[type] || type;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Dijital Kütüphane' : 'Digital Library'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {language === 'tr' ? '📚 Dijital İçerik Kütüphanesi' : '📚 Digital Content Library'}
            </h1>
            <p className="text-gray-600">
              {language === 'tr' 
                ? '3. parti yayıncılardan gelen eğitim kaynaklarını yönetin' 
                : 'Manage educational resources from third-party publishers'}
            </p>
          </div>
          
          {user?.role === 'admin' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition"
            >
              ➕ {language === 'tr' ? 'Kaynak Ekle' : 'Add Resource'}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {language === 'tr' ? 'Filtreler' : 'Filters'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {language === 'tr' ? 'Kaynak Tipi' : 'Resource Type'}
              </label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{language === 'tr' ? 'Tümü' : 'All'}</option>
                <option value="book">{language === 'tr' ? 'Kitap' : 'Book'}</option>
                <option value="pdf">PDF</option>
                <option value="video">{language === 'tr' ? 'Video' : 'Video'}</option>
                <option value="interactive">{language === 'tr' ? 'İnteraktif' : 'Interactive'}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {language === 'tr' ? 'Ders' : 'Subject'}
              </label>
              <select
                value={filters.subject}
                onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{language === 'tr' ? 'Tümü' : 'All'}</option>
                <option value="Physics">{language === 'tr' ? 'Fizik' : 'Physics'}</option>
                <option value="Chemistry">{language === 'tr' ? 'Kimya' : 'Chemistry'}</option>
                <option value="Biology">{language === 'tr' ? 'Biyoloji' : 'Biology'}</option>
                <option value="Mathematics">{language === 'tr' ? 'Matematik' : 'Mathematics'}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {language === 'tr' ? 'Seviye' : 'Level'}
              </label>
              <select
                value={filters.level}
                onChange={(e) => setFilters({ ...filters, level: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{language === 'tr' ? 'Tümü' : 'All'}</option>
                <option value="beginner">{language === 'tr' ? 'Başlangıç' : 'Beginner'}</option>
                <option value="intermediate">{language === 'tr' ? 'Orta' : 'Intermediate'}</option>
                <option value="advanced">{language === 'tr' ? 'İleri' : 'Advanced'}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
            </div>
          ) : resources.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500 text-lg">
                {language === 'tr' ? '📭 Henüz kaynak bulunmuyor' : '📭 No resources found'}
              </p>
              {user?.role === 'admin' && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="mt-4 text-blue-600 hover:text-blue-700 font-semibold"
                >
                  {language === 'tr' ? '➕ İlk kaynağı ekleyin' : '➕ Add the first resource'}
                </button>
              )}
            </div>
          ) : (
            resources.map((resource) => (
              <div
                key={resource.id}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-4xl">{getResourceIcon(resource.type)}</span>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                    {getResourceTypeName(resource.type)}
                  </span>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                  {resource.title}
                </h3>
                
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {resource.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                    {resource.subject}
                  </span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                    {resource.level}
                  </span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                    {resource.language}
                  </span>
                </div>
                
                {resource.author && (
                  <p className="text-xs text-gray-500 mb-2">
                    ✍️ {resource.author}
                  </p>
                )}
                
                {resource.publisher && (
                  <p className="text-xs text-gray-500 mb-2">
                    🏢 {resource.publisher}
                  </p>
                )}
                
                <div className="flex gap-2 mt-4">
                  {resource.url && (
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-sm font-semibold text-center transition"
                    >
                      {language === 'tr' ? '🔗 Aç' : '🔗 Open'}
                    </a>
                  )}
                  
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => handleDeleteResource(resource.id)}
                      className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 rounded-lg text-sm font-semibold transition"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Resource Modal */}
      {showAddModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowAddModal(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {language === 'tr' ? '➕ Yeni Dijital Kaynak Ekle' : '➕ Add New Digital Resource'}
            </h2>
            
            <form onSubmit={handleCreateResource} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Başlık' : 'Title'} *
                  </label>
                  <input
                    type="text"
                    required
                    value={newResource.title}
                    onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Açıklama' : 'Description'} *
                  </label>
                  <textarea
                    required
                    rows="3"
                    value={newResource.description}
                    onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  ></textarea>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Tip' : 'Type'} *
                  </label>
                  <select
                    required
                    value={newResource.type}
                    onChange={(e) => setNewResource({ ...newResource, type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="book">{language === 'tr' ? 'Kitap' : 'Book'}</option>
                    <option value="pdf">PDF</option>
                    <option value="video">{language === 'tr' ? 'Video' : 'Video'}</option>
                    <option value="interactive">{language === 'tr' ? 'İnteraktif' : 'Interactive'}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Kategori' : 'Category'} *
                  </label>
                  <select
                    required
                    value={newResource.category}
                    onChange={(e) => setNewResource({ ...newResource, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="textbook">{language === 'tr' ? 'Ders Kitabı' : 'Textbook'}</option>
                    <option value="reference">{language === 'tr' ? 'Referans' : 'Reference'}</option>
                    <option value="practice">{language === 'tr' ? 'Uygulama' : 'Practice'}</option>
                    <option value="lecture">{language === 'tr' ? 'Ders' : 'Lecture'}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Ders' : 'Subject'} *
                  </label>
                  <select
                    required
                    value={newResource.subject}
                    onChange={(e) => setNewResource({ ...newResource, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Physics">{language === 'tr' ? 'Fizik' : 'Physics'}</option>
                    <option value="Chemistry">{language === 'tr' ? 'Kimya' : 'Chemistry'}</option>
                    <option value="Biology">{language === 'tr' ? 'Biyoloji' : 'Biology'}</option>
                    <option value="Mathematics">{language === 'tr' ? 'Matematik' : 'Mathematics'}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Seviye' : 'Level'} *
                  </label>
                  <select
                    required
                    value={newResource.level}
                    onChange={(e) => setNewResource({ ...newResource, level: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="beginner">{language === 'tr' ? 'Başlangıç' : 'Beginner'}</option>
                    <option value="intermediate">{language === 'tr' ? 'Orta' : 'Intermediate'}</option>
                    <option value="advanced">{language === 'tr' ? 'İleri' : 'Advanced'}</option>
                  </select>
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Konular (virgülle ayırın)' : 'Topics (comma-separated)'} *
                  </label>
                  <input
                    type="text"
                    required
                    value={newResource.topics}
                    onChange={(e) => setNewResource({ ...newResource, topics: e.target.value })}
                    placeholder="Newton Laws, Dynamics, Forces"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Yazar' : 'Author'}
                  </label>
                  <input
                    type="text"
                    value={newResource.author}
                    onChange={(e) => setNewResource({ ...newResource, author: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Yayıncı' : 'Publisher'}
                  </label>
                  <input
                    type="text"
                    value={newResource.publisher}
                    onChange={(e) => setNewResource({ ...newResource, publisher: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    URL
                  </label>
                  <input
                    type="url"
                    value={newResource.url}
                    onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
                    placeholder="https://example.com/resource"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    ISBN
                  </label>
                  <input
                    type="text"
                    value={newResource.isbn}
                    onChange={(e) => setNewResource({ ...newResource, isbn: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Dil' : 'Language'} *
                  </label>
                  <select
                    required
                    value={newResource.language}
                    onChange={(e) => setNewResource({ ...newResource, language: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="TR">{language === 'tr' ? 'Türkçe' : 'Turkish'}</option>
                    <option value="EN">{language === 'tr' ? 'İngilizce' : 'English'}</option>
                    <option value="both">{language === 'tr' ? 'İki Dilli' : 'Both'}</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold transition"
                >
                  {language === 'tr' ? 'İptal' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition"
                >
                  {language === 'tr' ? '✅ Kaydet' : '✅ Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalLibraryPage;
